import { FC, useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import Button from 'src/components/ui/Button/Button';
import { FormInput } from 'src/components/ui/FormInput/FormInput';
import { FormPhoneInput } from 'src/components/ui/FormPhoneInput/FormPhoneInput';
import Input from 'src/components/ui/Input/Input';
import PageHeader from 'src/components/ui/PageHeader/PageHeader';
import { HTMLCountrySelectElement } from 'src/components/ui/PhoneInput/PhoneInput';
import { formRules, maxLengthRules } from 'src/constants/formConstants';
import { createTicket } from 'src/store/actions/ticketActions';
import { Address } from 'src/store/models/Auth';
import countries from 'src/utils/countries';
import { useCountryPhoneSelect } from 'src/utils/useCountryPhoneSelect';
import { useErrorSelector } from 'src/utils/useLoadingSelector';

import classes from './CreateNewTicket.module.scss';

export interface CreateTicketFormData {
  title: string;
  address: Address;
  eMail: string;
  phone: string;
  model: string;
  unitId: string;
  text: string;
}

const defaultValues: CreateTicketFormData = {
  title: '',
  address: {
    line1: '',
    line2: '',
    zip: '',
    city: '',
    country: '',
  },
  eMail: '',
  phone: '',
  model: '',
  unitId: '',
  text: '',
};

const CreateNewTicket: FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const phoneSelectRef = useRef<HTMLCountrySelectElement>(null);

  const user = useAppSelector((state) => state.auth.user);
  const createError = useErrorSelector<CreateTicketFormData>({
    actionType: createTicket.toString(),
  });

  const { control, handleSubmit, formState, watch } =
    useForm<CreateTicketFormData>({
      mode: 'onChange',
      defaultValues,
    });
  const notes = watch('text');
  const country = watch('address.country');

  useCountryPhoneSelect(country, phoneSelectRef?.current);

  const onSubmit: SubmitHandler<CreateTicketFormData> = (formData) => {
    dispatch(createTicket(formData));
  };

  return (
    <div className={classes.Container}>
      <PageHeader
        title={t('tickets:create_new')}
        showBackButton
        backConfirmationText={
          formState.isDirty && t('tickets:changes_will_not_save_create')
        }
        endNode={
          <Button
            type="submit"
            form="new-ticket-form"
            className={classes.HeaderButton}
            disabled={!formState.isValid}
          >
            {t('common:create')}
          </Button>
        }
      />
      <form noValidate id="new-ticket-form" onSubmit={handleSubmit(onSubmit)}>
        <h4>{t('tickets:customer_details')}</h4>
        <section>
          <FormInput<CreateTicketFormData>
            control={control}
            name="title"
            placeholder={t('tickets:enter_customer_name')}
            label={t('common:name')}
            rules={{ ...formRules, ...maxLengthRules() }}
            error={!!createError?.title}
            helperText={createError?.title}
          />
          <div />
          <div />
          <FormInput<CreateTicketFormData>
            control={control}
            name="address.line1"
            placeholder={t('common:enter_street_name')}
            label={t('common:address_line_1')}
            rules={{ ...formRules, ...maxLengthRules() }}
            error={!!createError?.address}
            helperText={createError?.address}
          />
          <FormInput<CreateTicketFormData>
            control={control}
            name="address.line2"
            placeholder={t('common:enter_additional_address')}
            label={`${t('common:address_line_2')} (${t(
              'common:optional'
            ).toLowerCase()})`}
            rules={maxLengthRules()}
          />
          <div />
          <FormInput<CreateTicketFormData>
            control={control}
            name="address.zip"
            placeholder={t('common:enter_postcode')}
            label={t('common:zip_postcode')}
            rules={{ ...formRules, ...maxLengthRules() }}
          />
          <FormInput<CreateTicketFormData>
            control={control}
            name="address.city"
            placeholder={t('common:enter_town_city')}
            label={t('common:city')}
            rules={{ ...formRules, ...maxLengthRules() }}
          />
          <FormInput<CreateTicketFormData>
            control={control}
            name="address.country"
            label={t('common:country')}
            rules={formRules}
            inputProps={{
              select: true,
              children: (
                <>
                  <option disabled value={''}>{`${t(
                    'common:choose'
                  )}...`}</option>
                  {Object.keys(countries?.getNames('en'))?.map((v, i) => (
                    <option key={i} value={v}>
                      {countries?.getNames('en')?.[v]}
                    </option>
                  ))}
                </>
              ),
            }}
          />
          <FormInput<CreateTicketFormData>
            control={control}
            name="eMail"
            type="email"
            placeholder={t('common:enter_email')}
            label={t('common:email')}
            rules={{ ...formRules, ...maxLengthRules() }}
            error={!!createError?.eMail}
            helperText={createError?.eMail}
          />
          <FormPhoneInput<CreateTicketFormData>
            control={control}
            name="phone"
            placeholder={t('common:enter_phone')}
            label={t('common:phone')}
            rules={{ ...formRules, ...maxLengthRules() }}
            error={!!createError?.phone}
            helperText={createError?.phone}
            selectRef={phoneSelectRef}
          />
        </section>
        <h4>{t('tickets:appliance_details')}</h4>
        <section>
          <Input
            value={user?.company?.title}
            id={'manufacturer'}
            label={t('common:manufacturer')}
            placeholder={t('common:manufacturer')}
            disabled
          />
          <FormInput<CreateTicketFormData>
            control={control}
            name="model"
            placeholder={t('tickets:specify_model')}
            label={t('common:model')}
            rules={{ ...formRules, ...maxLengthRules() }}
            error={!!createError?.model}
            helperText={createError?.model}
          />
          <FormInput<CreateTicketFormData>
            control={control}
            name="unitId"
            placeholder={t('tickets:enter_mac')}
            label={t('common:device_mac')}
            rules={{ ...formRules, ...maxLengthRules(12) }}
            error={!!createError?.unitId}
            helperText={createError?.unitId}
          />
        </section>
        <h4>{t('common:other_details')}</h4>
        <FormInput<CreateTicketFormData>
          control={control}
          name="text"
          placeholder={t('tickets:make_a_note')}
          label={t('common:notes')}
          inputProps={{
            fullWidth: true,
            multiline: true,
            rows: 5,
            inputProps: { maxLength: 500 },
          }}
          className={classes.Multiline}
          error={!!createError?.text}
          helperText={`${notes.length}/500`}
        />
      </form>
    </div>
  );
};

export default CreateNewTicket;
